import { useMemo } from 'react';
import { ChipMetadata } from '@pmi/dsm-react';
import ResponsiveTable from '../../../../../../shared/components/ResponsiveTable';
import { SortedColumn } from '../../../../../../shared/components/ResponsiveTable/models';
import styles from '../../UsersList/userslist.module.scss';
import { AgreementApiModel } from '../../../../../../shared/models/DealModel';
import usePermissions from '../../../../../../shared/hooks/usePermissions';
import PERMISSIONS from '../../../../../../shared/permissions';
import { User } from '../../../../../../shared/models/UserModels';
import { mapPaymentArrangementText } from '../../../../../../shared/constants';

export interface PreviousProductLogTableProps {
  user: User;
  productsList: any[];
  isLoading: boolean;
  accessibleAgreements: AgreementApiModel[];
}

export const getColumns = (
  accessibleAgreements: AgreementApiModel[],
): SortedColumn[] => [
  {
    name: 'product',
    title: 'Product',
    cell: (cntx) => <span>{cntx.row.original?.name}</span>,
    width: 5,
    sortingEnabled: false,
  },
  {
    name: 'assignedOn',
    title: 'Assigned On',
    cell: (cntx) => <span>{cntx.row.original?.assignedOn}</span>,
    width: 5,
    sortingEnabled: false,
  },
  {
    name: 'enrolledOn',
    title: 'Enrolled On',
    cell: (cntx) => <span>{cntx.row.original?.enrolledOn}</span>,
    width: 12,
    sortingEnabled: false,
  },
  {
    name: 'examDate',
    title: 'Exam Date',
    cell: (cntx) => (
      <span>
        {mapPaymentArrangementText(
          accessibleAgreements.find(
            (a) => a.id === cntx.row.original?.examDate,
          ),
        )}
      </span>
    ),
    width: 12,
    sortingEnabled: false,
  },
  {
    name: 'agreementId',
    title: 'Agreement ID',
    cell: (cntx) => (
      <span>
        {mapPaymentArrangementText(
          accessibleAgreements.find((a) => a.id === cntx.row.original.dealId),
        )}
      </span>
    ),
    width: 12,
    sortingEnabled: false,
  },
];

export default function PreviousProductLogTable({
  user,
  productsList = [],
  isLoading,
  accessibleAgreements,
}: PreviousProductLogTableProps) {
  const { hasAccess, isLoading: isAccessLoading } = usePermissions();

  const noData = !isLoading && productsList.length < 1;
  const isInternalAdmin = hasAccess({
    permissions: [PERMISSIONS.ManageElevatedAdmins],
  });
  const columns = useMemo(
    () => getColumns(accessibleAgreements),
    [isLoading, accessibleAgreements, isAccessLoading],
  );

  if ((!isAccessLoading && !isInternalAdmin) || user.isRemoved) return null;

  return (
    <div>
      <div className="d-flex align-items-center w-100">
        <div className="d-flex align-items-center flex-wrap">
          <h5 className="mb-0 me-1">Previous Products Log</h5>
          <ChipMetadata
            variant="gray"
            text={` ${productsList?.length || 0} product${
              productsList?.length > 1 ? 's' : ''
            }`}
          />
        </div>
      </div>
      <p className="mb-2 mt-2">
        This is the history of products that have been assigned to this person’s
        account.
      </p>
      <div
        className={`${styles.usersTableContainer} overflow-auto thead-bg-gray`}
        data-testid="user-list"
      >
        {!isLoading && (
          <ResponsiveTable
            data={productsList}
            columns={columns}
            className={`overflow-auto ${styles.tableUsers}`}
          />
        )}
        {noData && (
          <div
            className={`d-block my-5 text-center mx-auto w-75 ${styles.noResult}`}
          >
            No Data to Display...
          </div>
        )}
        {isLoading && <p className="py-2">Loading...</p>}
      </div>
    </div>
  );
}
